import { useDashboardStats } from '../../../hooks/useDashboardStats'
import DashboardCard from '../shared/DashboardCard'
import StatCard from '../shared/StatCard'
import QuickActions from '../shared/QuickActions'
import RealTimeResults from '../shared/RealTimeResults'
import IncompleteTotalsBanner from '../shared/IncompleteTotalsBanner'
import {
  CalendarIcon,
  UserGroupIcon,
  ChartBarIcon,
  MapPinIcon,
  ClockIcon,
} from '@heroicons/react/24/outline'

/**
 * "Not readable" and "zero" must not render the same way. When the stats hook
 * hard-fails it returns null, and a tile showing `0` would be a confident claim
 * about data we never saw.
 */
const UNAVAILABLE = '—'

const statValue = (value: number | null | undefined): string | number =>
  value === null || value === undefined ? UNAVAILABLE : value

const percent = (numerator: number, denominator: number) =>
  `${Math.round((numerator / denominator) * 1000) / 10}%`

export default function EventOrganizerDashboard() {
  const { stats, loading, error } = useDashboardStats()

  const eventStats = [
    {
      name: 'My Events',
      value: statValue(stats?.totalEvents),
      change: stats?.changes.totalEvents,
      icon: CalendarIcon,
    },
    {
      name: 'Active Events',
      value: statValue(stats?.activeEvents),
      change: stats?.changes.activeEvents,
      icon: ClockIcon,
    },
    {
      name: 'Total Participants',
      value: statValue(stats?.totalAthletes),
      change: stats?.changes.totalAthletes,
      icon: UserGroupIcon,
    },
    {
      name: 'Results Recorded',
      value: statValue(stats?.totalResults),
      change: stats?.changes.totalResults,
      icon: ChartBarIcon,
    },
  ]

  // Real events from `GET /api/events`, narrowed to the ones still ahead and
  // ordered soonest-first. Previously this whole list was a hardcoded literal
  // ("Spring Football Combine / Memorial Stadium / 45 registered") that had no
  // relationship to the signed-in organizer's account (#159).
  const now = Date.now()
  const upcomingEvents = (stats?.events ?? [])
    .filter(event => {
      const start = new Date(event.startDate).getTime()
      return !Number.isNaN(start) && start >= now
    })
    .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
    .slice(0, 5)

  const recentActivity = stats?.recentActivity ?? []

  const participation = stats?.participation ?? null
  const hasParticipation = participation !== null && participation.total > 0

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'SCHEDULED':
        return 'bg-green-100 text-green-800'
      case 'DRAFT':
        return 'bg-yellow-100 text-yellow-800'
      case 'ACTIVE':
        return 'bg-blue-100 text-blue-800'
      case 'CANCELLED':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">Event Organizer Dashboard</h1>
        <p className="mt-1 text-sm text-gray-600">
          Manage your combines and track participant performance
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4" role="alert">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {stats && <IncompleteTotalsBanner gaps={stats.gaps} />}

      {/* Event Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {eventStats.map((stat, index) => (
          <StatCard
            key={index}
            name={stat.name}
            value={stat.value}
            change={stat.change}
            icon={stat.icon}
            loading={loading}
          />
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upcoming Events */}
        <div className="lg:col-span-2">
          <DashboardCard
            title="Upcoming Events"
            loading={loading}
            error={error ?? undefined}
            actions={
              <button
                className="btn-primary text-sm"
                onClick={() => (window.location.href = '/events/create')}
              >
                Create Event
              </button>
            }
          >
            {upcomingEvents.length === 0 ? (
              <p className="py-4 text-center text-sm text-gray-500">
                No upcoming events. Create one to get started.
              </p>
            ) : (
              <div className="space-y-4">
                {upcomingEvents.map(event => (
                  <div
                    key={event.id}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900">{event.name}</h4>
                        <div className="flex items-center mt-1 space-x-4 text-xs text-gray-500">
                          <div className="flex items-center">
                            <CalendarIcon className="h-4 w-4 mr-1" />
                            {new Date(event.startDate).toLocaleDateString()}
                          </div>
                          <div className="flex items-center">
                            <MapPinIcon className="h-4 w-4 mr-1" />
                            {event.location}
                          </div>
                          <div className="flex items-center">
                            <UserGroupIcon className="h-4 w-4 mr-1" />
                            {event.participantCount} registered
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(event.status)}`}
                        >
                          {event.status.toLowerCase().replace(/_/g, ' ')}
                        </span>
                        <button className="text-primary-600 hover:text-primary-900 text-sm">
                          Manage
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DashboardCard>
        </div>

        {/* Quick Actions */}
        <DashboardCard title="Quick Actions">
          <QuickActions />
        </DashboardCard>
      </div>

      {/* Bottom Section with Real-Time Data */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <DashboardCard title="Recent Activity" loading={loading} error={error ?? undefined}>
          {recentActivity.length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-500">No recent activity to display</p>
          ) : (
            <div className="space-y-4">
              {recentActivity.slice(0, 5).map(activity => (
                <div key={activity.id} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-2 h-2 rounded-full bg-blue-400 mt-2" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                    <p className="text-sm text-gray-500">{activity.description}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(activity.timestamp).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DashboardCard>

        {/*
          Event Performance — only the two rates that are actually derivable.

          This card used to print "94% completion / 6% no-show / 4.8-out-of-5
          athlete satisfaction / < 2 hours results processing", none of it
          computed from anything (#159). Completion and no-show ARE derivable
          from the participation buckets on `GET /api/analytics/events/:id`, so
          they are computed here and kept.

          Dropped, because no endpoint and no data model backs them:
          - Athlete Satisfaction — there is no survey, rating, or feedback model
            anywhere in the schema. Nothing could ever populate it.
          - Results Processing Time — no timestamp pair is recorded between a
            result being captured and being published.
        */}
        <DashboardCard title="Event Performance" loading={loading} error={error ?? undefined}>
          {!hasParticipation ? (
            <p className="py-4 text-center text-sm text-gray-500">No participation recorded yet.</p>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Average Completion Rate</span>
                <span className="text-sm font-medium text-green-600">
                  {percent(participation.completed, participation.total)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">No-Show Rate</span>
                <span className="text-sm font-medium text-yellow-600">
                  {percent(participation.noShow, participation.total)}
                </span>
              </div>
              <p className="pt-2 text-xs text-gray-400">
                Across {participation.total.toLocaleString()} participant entries in your events.
              </p>
            </div>
          )}
        </DashboardCard>

        {/* Live Results — no event preselected at the dashboard level. The
            socket still connects, but without an eventId it joins no room, so
            the widget sits in its connected/idle "waiting for live results"
            state instead of erroring on a nonexistent 'sample-event-id' room.
            The per-event live feed lives in the Event Command Center. */}
        <RealTimeResults maxResults={8} showConnectionStatus={true} />
      </div>
    </div>
  )
}
